import { useState } from 'react';
import { Card, Title, Text, Button, TextInput } from '@tremor/react';
import MinMaxParamsForm from './MinMaxParamsForm';
import {
  previewMinMax, applyMinMax, defaultMinMaxParams, validateMinMaxParams, parseLowestMin,
} from '../api/minmax';
import type { MinMaxParams, MinMaxProposal, MinMaxSkipped, User } from '../api/types';
import { t } from '../lib/i18n';
import { canMinMax } from '../lib/roles';

/**
 * JCD-56 Phase 2 — recalculate one part's min/max from its own page.
 *
 * The proposed levels are editable before applying: overriding a suggestion
 * down a list is what mnmxedit.p does natively, and an edited row is flagged
 * `overridden` so the audit distinguishes the engine's proposal from a
 * person's choice.
 *
 * An empty result explains itself rather than showing a blank box — the
 * engine reports only what it would change, so "no proposals" usually means
 * the levels already match.
 */
interface Props {
  sid: number;
  line: string;
  part: string;
  user: User;
  writesEnabled: boolean;
  onApplied: () => void;
}

export default function PartMinMaxPanel({
  sid, line, part, user, writesEnabled, onApplied,
}: Props) {
  const canRun = writesEnabled && canMinMax(user);

  const [params, setParams] = useState<MinMaxParams>(defaultMinMaxParams());
  const [lowestMinText, setLowestMinText] = useState('');
  const [proposal, setProposal] = useState<MinMaxProposal | null>(null);
  const [scanned, setScanned] = useState<number | null>(null);
  const [skipped, setSkipped] = useState<MinMaxSkipped | null>(null);
  const [minText, setMinText] = useState('');
  const [maxText, setMaxText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  if (!canRun) return null;

  async function onCalc() {
    setErr(null);
    setOkMsg(null);
    setProposal(null);
    setScanned(null);
    setSkipped(null);

    if (parseLowestMin(lowestMinText) === null) {
      setErr(t.part.minmax.err.lowestMin);
      return;
    }
    const bad = validateMinMaxParams(params);
    if (bad) {
      setErr((t.part.minmax.err as Record<string, string>)[bad] ?? bad);
      return;
    }

    setBusy(true);
    try {
      const res = await previewMinMax(
        { sid, lines: line, only_part: part, stock: 'B', skip_no_history: false, skip_manual: false },
        params,
        undefined,
        1,
      );
      setScanned(res.scanned);
      setSkipped(res.skipped);
      if (res.proposals.length > 0) {
        const p = res.proposals[0];
        setProposal(p);
        setMinText(String(p.new_min));
        setMaxText(String(p.new_max));
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function onApply() {
    if (!proposal) return;
    setBusy(true);
    setErr(null);
    try {
      const min = Number(minText);
      const max = Number(maxText);
      if (!Number.isInteger(min) || !Number.isInteger(max) || min < 0 || max < 0) {
        setErr(t.part.edit.errPositive);
        return;
      }
      await applyMinMax(
        params.season,
        [{
          sid, line, part, min, max,
          overridden: min !== proposal.new_min || max !== proposal.new_max,
        }],
        params,
      );
      setOkMsg(t.part.minmax.applied);
      setProposal(null);
      onApplied();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  // The engine reports only changes, so nothing came back for one of two
  // reasons: the levels already match, or the filters excluded the part.
  const emptyReason =
    proposal === null && scanned !== null
      ? scanned > 0
        ? t.part.minmax.noChange
        : t.part.minmax.filtered
      : null;

  return (
    <Card className="mt-4">
      <Title>{t.part.minmax.title}</Title>

      <MinMaxParamsForm
        value={params}
        onChange={setParams}
        lowestMinText={lowestMinText}
        onLowestMinTextChange={setLowestMinText}
        disabled={busy}
      />

      <Button className="mt-4" onClick={onCalc} disabled={busy}>
        {t.part.minmax.calc}
      </Button>

      {err && <Text color="red" className="mt-3">{err}</Text>}
      {okMsg && <Text color="green" className="mt-3">{okMsg}</Text>}
      {emptyReason && <Text className="mt-3">{emptyReason}</Text>}

      {proposal && (
        <div className="mt-4 border-t pt-3">
          <Text>
            {t.part.minmax.histUsed}: {proposal.hist_qty}
          </Text>
          <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-4">
            <div>
              <label htmlFor="mm-new-min" className="block text-sm">
                {t.part.edit.f.min1} ({proposal.cur_min} {t.part.minmax.curArrow})
              </label>
              <TextInput
                id="mm-new-min"
                value={minText}
                disabled={busy}
                onChange={(e) => setMinText(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="mm-new-max" className="block text-sm">
                {t.part.edit.f.max1} ({proposal.cur_max} {t.part.minmax.curArrow})
              </label>
              <TextInput
                id="mm-new-max"
                value={maxText}
                disabled={busy}
                onChange={(e) => setMaxText(e.target.value)}
              />
            </div>
          </div>
          <Button className="mt-3" onClick={onApply} disabled={busy}>
            {t.part.minmax.apply}
          </Button>
        </div>
      )}

      {skipped && proposal === null && scanned === 0 && (
        <Text className="mt-2 text-xs">
          {`no_history ${skipped.no_history} · manual ${skipped.manual} · edate ${skipped.edate} · stock ${skipped.stock} · filtros ${skipped.filters}`}
        </Text>
      )}
    </Card>
  );
}
